import { Component } from 'react'
import { Alert } from 'reactstrap'
import moment from 'moment-timezone'
import IncidentList from './IncidentsList'
import IncidentSubNav from './IncidentSubNav'
import { buildPresets } from '../Filters/presetFilters'
import './Incidents.css'

// Both lookups here are page policy rather than user filters -- this tab has no
// Advanced Filters popover and renders no chips -- so every preset is invisible
// and locked. `id not_in` and the relation values are relay global ids; the
// filter engine decodes them.
const policyPreset = (fieldName, filterType, filterValues) => ({ fieldName, filterType, filterValues, editable: false, visible: false })

// Never the incident being viewed.
const excludingSelf = (excludeIncidentIds) =>
    excludeIncidentIds && excludeIncidentIds.length ? [policyPreset("id", "not_in", excludeIncidentIds)] : []

// dateAdded arrives as an ISO timestamp in UTC. The calendar day that matters is
// the New York one (settings.TIME_ZONE), which is also how the backend reads a
// bare date against a datetime column -- so both ends agree on the boundary.
const newYorkDay = (isoTimestamp) => (isoTimestamp ? moment(isoTimestamp).tz("America/New_York").format("YYYY-MM-DD") : null)

// Shared by the Duplicates and Related views: literally the same day, and never
// the incident being viewed.
const sameDayExcludingSelf = (incidentDate, excludeIncidentIds) => [
    policyPreset("incident_date", "equal", incidentDate),
    ...excludingSelf(excludeIncidentIds),
]

// The duplicate views are all built from stock filter fields, which the engine
// ANDs together. The pairing rule behind them is symmetric: a dated incident and
// an undated one are duplicates when the undated one was added on or after the
// dated one's DOL. Which side you can look from depends on whether the incident
// being viewed has a DOL, so the two cross-DOL views are mutually exclusive and
// only ever one of them is offered.

// Duplicates: same car, same day. Needs a DOL on the incident being viewed.
const sameDayDuplicatePresets = (carId, incidentDate, excludeIncidentIds) => [
    policyPreset("car", "equal", carId),
    ...sameDayExcludingSelf(incidentDate, excludeIncidentIds),
]

// The driver is matched on both cross-DOL views: a report on the other side of
// the DOL divide is thin evidence on its own, so the extra agreement is worth
// requiring there even though the same-day Duplicates view doesn't. Skipped when
// the incident being viewed has no driver recorded -- there is nothing to match,
// and dropping candidates for a blank field would hide real duplicates.
const matchingDriver = (driverId) => (driverId ? [policyPreset("driver", "equal", driverId)] : [])

// Duplicates (With DOL), offered when the incident being viewed has NO DOL: the
// dated incidents for the car it could belong to, i.e. any DOL up to the day this
// incident was logged.
const datedDuplicatePresets = (carId, driverId, addedOnDay, excludeIncidentIds) => [
    policyPreset("car", "equal", carId),
    ...matchingDriver(driverId),
    policyPreset("incident_date", "less_than_or_equal", addedOnDay),
    ...excludingSelf(excludeIncidentIds),
]

// Duplicates (No DOL), offered when the incident being viewed HAS a DOL: the
// undated incidents for the car that were added on or after it -- a duplicate
// filed later often never gets its DOL set.
const undatedDuplicatePresets = (carId, driverId, incidentDate, excludeIncidentIds) => [
    policyPreset("car", "equal", carId),
    ...matchingDriver(driverId),
    policyPreset("incident_date", "is_null", null),
    policyPreset("date_added", "greater_than_or_equal", incidentDate),
    ...excludingSelf(excludeIncidentIds),
]

// Columns most useful for spotting related/duplicate incidents.
const RELATED_INCIDENTS_COLUMNS = [
    { id: "id", name: "Date Added", sortable: true, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "duplicateReviewStatus", name: "Review Status", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "title", name: "Incident Name", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "incidentDate", name: "Incident Date", sortable: true, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "incidentTime", name: "Incident Time", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "status", name: "Status", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "source", name: "Source", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "driver__id", name: "Driver", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "car__id", name: "Car", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "hasLiabilityClaim", name: "Liability Claim?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "hasSubroClaim", name: "Subro Claim?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "isArchived", name: "Archived?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
].map((column, index) => ({ ...column, order: index }))

// The Related view lists incidents on the OTHER vehicle, which are never duplicates of
// this one, so it offers no transfer or duplicate review -- and without the review
// actions the list does not load review metadata, so the column would always be blank.
const OTHER_VEHICLE_INCIDENTS_COLUMNS = RELATED_INCIDENTS_COLUMNS
    .filter((column) => column.id !== "duplicateReviewStatus")
    .map((column, index) => ({ ...column, order: index }))

const VIEWS = {
    DUPLICATES: "duplicates",
    WITH_DOL: "withDol",
    NO_DOL: "noDol",
    RELATED: "related",
}

// Every No DOL row has a null incident date, so Date Added (the "id" column) is
// the only sort that means anything there.
const defaultOrderBy = (view) => (view === VIEWS.NO_DOL ? ["-id"] : ["-incidentDate"])

class RelatedIncidentsTab extends Component {
    constructor(props) {
        super(props)
        this.state = {
            view: VIEWS.DUPLICATES,
            orderBy: defaultOrderBy(VIEWS.DUPLICATES),
            selectedIncidents: [],
            showBanner: true,
        }
    }

    updateOrder = (string) => {
        let order = this.state.orderBy ? [...this.state.orderBy] : []
        let index = order.indexOf(string)
        if (index > -1) {
            order[index] = "-" + string
        } else {
            index = order.indexOf("-" + string)
            index > -1 ? order.splice(index, 1) : order.unshift(string)
        }
        this.setState({ orderBy: order })
    }

    setView = (view) => {
        if (view !== this.state.view) {
            this.setState({ view, selectedIncidents: [], orderBy: defaultOrderBy(view) })
        }
    }

    renderDuplicatesView = () => {
        if (!this.props.carId) {
            return <Alert color="info" className="mt-3">This incident has no car attached, so potential duplicates cannot be looked up.</Alert>
        }
        if (!this.props.incidentDate) {
            return <Alert color="info" className="mt-3">This incident has no date, so potential duplicates on the same day cannot be looked up. See the <strong>Duplicates (With DOL)</strong> tab for the incidents this one could belong to.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    Incidents below belong to the <strong>same car on the same day</strong> — likely duplicates. Open any incident to archive, delete, or transfer its data.
                </Alert>
                <IncidentList
                    columnConfig={RELATED_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    presets={buildPresets(sameDayDuplicatePresets(
                        this.props.carId,
                        this.props.incidentDate,
                        this.props.excludeIncidentIds,
                    ))}
                    transferTargetIncident={this.props.currentIncident}
                    showDuplicateReviewActions
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    // The mirror of the No DOL view, offered only when the incident being viewed
    // has no DOL of its own: the dated incidents it could be a duplicate OF.
    renderWithDolView = () => {
        const addedOnDay = newYorkDay(this.props.dateAdded)
        if (!this.props.carId) {
            return <Alert color="info" className="mt-3">This incident has no car attached, so potential duplicates cannot be looked up.</Alert>
        }
        if (!addedOnDay) {
            return <Alert color="info" className="mt-3">This incident has no date added, so there is nothing to bound a search for dated duplicates by.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    This incident has <strong>no date of loss</strong>, so the incidents below are the ones it could belong to: the <strong>{this.props.driverId ? "same car and driver" : "same car"}</strong>, with a date of loss <strong>on or before the day this incident was added</strong> ({addedOnDay}).
                    {!this.props.driverId && " This incident has no driver recorded, so the driver is not being matched on."}
                    {" "}Open any incident to archive, delete, or transfer its data.
                </Alert>
                <IncidentList
                    columnConfig={RELATED_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    presets={buildPresets(datedDuplicatePresets(
                        this.props.carId,
                        this.props.driverId,
                        addedOnDay,
                        this.props.excludeIncidentIds,
                    ))}
                    transferTargetIncident={this.props.currentIncident}
                    showDuplicateReviewActions
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    // Duplicates that never got a date of loss: same car and driver, added on or
    // after this incident's DOL. Offered only when the incident being viewed has
    // one -- without it there is no bound, and the With DOL view is the lookup.
    renderNoDolView = () => {
        if (!this.props.carId) {
            return <Alert color="info" className="mt-3">This incident has no car attached, so potential duplicates cannot be looked up.</Alert>
        }
        if (!this.props.incidentDate) {
            return <Alert color="info" className="mt-3">This incident has no date of loss, so undated duplicates cannot be bounded. See the <strong>Duplicates (With DOL)</strong> tab instead.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    Incidents below belong to the <strong>{this.props.driverId ? "same car and driver" : "same car"}</strong>, have <strong>no date of loss</strong>, and were added on or after this incident's date — likely duplicates reported after the fact.
                    {!this.props.driverId && " This incident has no driver recorded, so the driver is not being matched on."}
                    {" "}Open any incident to archive, delete, or transfer its data.
                </Alert>
                <IncidentList
                    columnConfig={RELATED_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    presets={buildPresets(undatedDuplicatePresets(
                        this.props.carId,
                        this.props.driverId,
                        this.props.incidentDate,
                        this.props.excludeIncidentIds,
                    ))}
                    transferTargetIncident={this.props.currentIncident}
                    showDuplicateReviewActions
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    renderRelatedView = () => {
        // Match the trimming the legacy car_vins resolver arg did, so a party
        // VIN recorded with stray whitespace still matches.
        const vins = (this.props.otherVehicleVins || []).map((vin) => String(vin).trim()).filter(Boolean)
        if (!this.props.incidentDate) {
            return <Alert color="info" className="mt-3">This incident has no date, so related incidents on the same day cannot be looked up.</Alert>
        }
        if (!vins.length) {
            return <Alert color="info" className="mt-3">No other vehicle parties with a VIN are recorded on this incident, so related incidents cannot be looked up.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    Incidents below involve the <strong>other vehicle(s) from this incident</strong> (matched by VIN) on the <strong>same day</strong>.
                </Alert>
                <IncidentList
                    columnConfig={OTHER_VEHICLE_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    presets={buildPresets([
                        policyPreset("car_vin", "in", vins),
                        ...sameDayExcludingSelf(this.props.incidentDate, this.props.excludeIncidentIds),
                    ])}
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    renderActiveView = () => {
        if (this.state.view === VIEWS.DUPLICATES) {
            return this.renderDuplicatesView()
        }
        if (this.state.view === VIEWS.WITH_DOL) {
            return this.renderWithDolView()
        }
        if (this.state.view === VIEWS.NO_DOL) {
            return this.renderNoDolView()
        }
        return this.renderRelatedView()
    }

    render() {
        const { view } = this.state
        const duplicatesCount = this.props.duplicatesCount || 0
        const withDolCount = this.props.withDolCount || 0
        const noDolCount = this.props.noDolCount || 0
        const relatedCount = this.props.relatedCount || 0
        // The two cross-DOL lookups are mutually exclusive, so exactly one of them
        // is offered: from a dated incident you look for undated candidates, and
        // from an undated one for dated candidates.
        const crossDolItem = this.props.incidentDate
            ? { id: VIEWS.NO_DOL, label: "Duplicates (No DOL)", icon: "fa-calendar-times-o", count: this.props.noDolCount ?? null }
            : { id: VIEWS.WITH_DOL, label: "Duplicates (With DOL)", icon: "fa-calendar", count: this.props.withDolCount ?? null }
        const subNavItems = [
            { id: VIEWS.DUPLICATES, label: "Duplicates", icon: "fa-clone", count: this.props.duplicatesCount ?? null },
            crossDolItem,
            { id: VIEWS.RELATED, label: "Related", icon: "fa-link", count: this.props.relatedCount ?? null },
        ]
        // Duplicates and With DOL are mutually exclusive -- exactly one of them is
        // offered -- so their counts add up to one "prospective duplicates" figure.
        const datedCount = duplicatesCount + withDolCount
        const showBanner = this.state.showBanner && (datedCount > 0 || noDolCount > 0 || relatedCount > 0)
        return (
            <div className="related-tab-container">
                {showBanner && (
                    <div className="incident-overview-banner">
                        <span className="incident-overview-banner__icon"><i className="fa fa-info" aria-hidden="true" /></span>
                        <div className="incident-overview-banner__text">
                            <div className="incident-overview-banner__title">Potential Duplicate Records Detected</div>
                            <div className="incident-overview-banner__desc">
                                Our system has identified {datedCount} prospective duplicate{datedCount === 1 ? "" : "s"}, {noDolCount} record{noDolCount === 1 ? "" : "s"} with no date of loss, and {relatedCount} linked historical record{relatedCount === 1 ? "" : "s"} with shared drivers, vehicle parameters, and overlapping incident timelines. Please review below.
                            </div>
                        </div>
                        <button type="button" className="incident-overview-banner__close" onClick={() => this.setState({ showBanner: false })} aria-label="Dismiss">
                            <i className="fa fa-times-circle" aria-hidden="true" />
                        </button>
                    </div>
                )}
                <div className="incident-overview-header">
                    <div className="incident-overview-header__titles">
                        <h3 className="incident-overview-header__title">Related &amp; Duplicates Overview</h3>
                        <p className="incident-overview-header__subtitle">Analyze, confirm, or archive identified collision cross-references</p>
                    </div>
                    <IncidentSubNav items={subNavItems} activeId={view} onSelect={this.setView} />
                </div>
                {this.renderActiveView()}
            </div>
        )
    }
}

export default RelatedIncidentsTab
